import React, { useEffect, useState } from 'react'
import styles from 'styles/components/TeamManagement.module.sass'
import { fetchOrgMembers, inviteOrgMember, revokeOrgMember, OrgMember, OrgMembersSummary } from 'api/TeamApiService'
import { useToast } from 'provider/ToastProvider'
import ZeroneLoader from 'components/common/ZeroneLoader'
import ReusableIcon from 'components/common/ReusableIcon'
import PrimaryButton from 'components/common/PrimaryButton'

/**
 * TeamManagement — shown in the Profile "Team" tab.
 * Lets org owners invite/revoke members and see seat usage.
 */
const TeamManagement: React.FC = () => {
    const { addToast } = useToast()
    const [summary, setSummary] = useState<OrgMembersSummary | null>(null)
    const [loading, setLoading] = useState(true)
    const [inviteEmail, setInviteEmail] = useState('')
    const [inviting, setInviting] = useState(false)
    const [revokingId, setRevokingId] = useState<string | null>(null)

    const load = async () => {
        setLoading(true)
        const data = await fetchOrgMembers()
        setSummary(data)
        setLoading(false)
    }

    useEffect(() => { void load() }, [])

    const handleInvite = async () => {
        if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
            addToast({ type: 'error', message: 'Please enter a valid email address.' })
            return
        }
        setInviting(true)
        const result = await inviteOrgMember(inviteEmail.trim().toLowerCase())
        setInviting(false)
        if (result.ok) {
            addToast({ type: 'success', message: 'Invite sent successfully.' })
            setInviteEmail('')
            void load()
        } else {
            addToast({ type: 'error', message: result.message ?? 'Could not send invite.' })
        }
    }

    const handleRevoke = async (member: OrgMember) => {
        setRevokingId(member.invitedUserId)
        const ok = await revokeOrgMember(member.invitedUserId)
        setRevokingId(null)
        if (ok) {
            addToast({ type: 'success', message: `${member.firstName} has been removed.` })
            void load()
        } else {
            addToast({ type: 'error', message: 'Could not remove member. Please try again.' })
        }
    }

    if (loading) {
        return (
            <div className={styles['loader']}>
                <ZeroneLoader text="Loading team..." />
            </div>
        )
    }

    if (!summary) {
        return (
            <div className={styles['empty']}>
                <p>Unable to load team data. Please try again later.</p>
            </div>
        )
    }

    const { members, seats } = summary

    if (seats.invitable === 0) {
        return (
            <div className={styles['team-management']}>
                <div className={styles['seats-card']}>
                    <h3 className={styles['seats-title']}>Team Members</h3>
                    <p className={styles['seats-note']}>
                        Your current plan does not include team seats. Upgrade to a paid plan to invite colleagues and collaborate on projects.
                    </p>
                </div>
            </div>
        )
    }

    const usedPercent = Math.round((seats.used / seats.invitable) * 100)

    return (
        <div className={styles['team-management']}>
            {/* Seat Usage */}
            <div className={styles['seats-card']}>
                <div className={styles['seats-header']}>
                    <h3 className={styles['seats-title']}>Seat Usage</h3>
                    <span className={styles['seats-count']}>{seats.used} / {seats.invitable} seats used</span>
                </div>
                <div className={styles['seats-bar-track']}>
                    <div
                        className={styles['seats-bar-fill']}
                        style={{ width: `${Math.min(usedPercent, 100)}%` }}
                    />
                </div>
                <p className={styles['seats-note']}>
                    {seats.remaining > 0
                        ? `${seats.remaining} seat${seats.remaining === 1 ? '' : 's'} remaining`
                        : 'All seats are taken. Upgrade your plan to add more team members.'}
                </p>
            </div>

            {/* Invite Input */}
            {seats.remaining > 0 && (
                <div className={styles['invite-section']}>
                    <h3 className={styles['section-title']}>Invite a Team Member</h3>
                    <p className={styles['section-note']}>
                        Enter the email of a registered ZeroneAnalytiQs user. If they don&apos;t have an account yet, they&apos;ll receive a link to create one.
                    </p>
                    <div className={styles['invite-row']}>
                        <input
                            type="email"
                            className={styles['invite-input']}
                            placeholder="team@example.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') void handleInvite() }}
                        />
                        <PrimaryButton
                            text={inviting ? 'Sending...' : 'Send Invite'}
                            width='auto'
                            primaryColor='#FF5522'
                            textColor='#FFFFFF'
                            hoverColor='#FFFFFF'
                            hoverTextColor='#FF5522'
                            doSomething={() => { if (!inviting) void handleInvite() }}
                        />
                    </div>
                </div>
            )}

            {/* Members List */}
            <div className={styles['members-section']}>
                <h3 className={styles['section-title']}>Team Members</h3>
                {members.length === 0 ? (
                    <p className={styles['empty-note']}>No members yet. Invite your team above.</p>
                ) : (
                    <div className={styles['members-list']}>
                        {members.map((member) => (
                            <div key={member.id} className={styles['member-row']}>
                                <div className={styles['member-avatar']}>
                                    {member.firstName.charAt(0).toUpperCase()}{member.lastName.charAt(0).toUpperCase()}
                                </div>
                                <div className={styles['member-info']}>
                                    <p className={styles['member-name']}>{member.firstName} {member.lastName}</p>
                                    <p className={styles['member-email']}>{member.email}</p>
                                </div>
                                <span className={`${styles['member-status']} ${styles[member.status]}`}>
                                    {member.status === 'pending' ? 'Invite Sent' : 'Active'}
                                </span>
                                <button
                                    className={styles['revoke-button']}
                                    onClick={() => void handleRevoke(member)}
                                    disabled={revokingId === member.invitedUserId}
                                    aria-label={`Remove ${member.firstName}`}
                                    title="Remove member"
                                >
                                    <ReusableIcon icon="mynaui:trash" color="inherit" className={styles['revoke-icon'] ?? 'revoke-icon'} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default TeamManagement
